import { driver, type Alignment, type DriveStep, type Driver, type PopoverDOM, type Side } from "driver.js";
import { logger } from "../logger";
import { localStore } from "../storage";
import { timers } from "../timers";
import { clearChildren, createElement } from "../utils/html";

type TourStepSpec = {
  target: string;
  title: string;
  description: string;
  side?: Side;
  align?: Alignment;
};

type HelpPanelElements = {
  root: HTMLElement;
  content: HTMLElement | null;
  search: HTMLInputElement | null;
};

type WindowWithTourState = Window & {
  __WMUToursBooted?: boolean;
  WMUToast?: (message: string, tone?: string) => void;
};

const HELP_ENDPOINT = "/api/help";
const TOUR_STORAGE_PREFIX = "wmu_tour_dismissed:";
const TOUR_SKIP_BUTTON_SELECTOR = "[data-tour-skip]";

const TOUR_STEPS: Record<string, TourStepSpec[]> = {
  qrcodes: [
    {
      target: '[data-tour="qr-filter"]',
      title: "Filter the QR surface",
      description: "Filter and saved views keep the QR table manageable during live operations.",
      side: "bottom",
    },
    {
      target: '[data-tour="qr-list"]',
      title: "Run the live QR queue",
      description: "This table is your operational source of truth for statuses, scans, and batch actions.",
      side: "top",
    },
    {
      target: '[data-tour="qr-create"]',
      title: "Create with context",
      description: "Use minimal create first, then open QR detail sections for schedule, access, prerequisites, and visual tuning.",
      side: "left",
    },
  ],
  planner: [
    {
      target: '[data-tour="planner-day"]',
      title: "Stay on the right event day",
      description: "Switch between event days to avoid editing the wrong day schedule.",
      side: "bottom",
    },
    {
      target: '[data-tour="planner-timeline"]',
      title: "Run the day from timeline",
      description: "Timeline entries orchestrate QR activations, quests, and staff actions.",
      side: "right",
    },
    {
      target: '[data-tour="planner-new"]',
      title: "Add the next block fast",
      description: "Use Add entry for precise activities; templates help you bootstrap faster.",
      side: "left",
    },
  ],
  participants: [
    {
      target: '[data-tour="tracker-filters"]',
      title: "Filter for people who need attention",
      description: "Filter by engagement and faction to quickly find who needs attention.",
      side: "bottom",
    },
    {
      target: '[data-tour="tracker-stats"]',
      title: "Watch the risk counters",
      description: "These counters show immediate engagement risk in the current page scope.",
      side: "bottom",
    },
    {
      target: '[data-tour="tracker-list"]',
      title: "Open the intervention list",
      description: "Open participant detail for interventions: token grants, notifications, and inventory actions.",
      side: "top",
    },
  ],
  webhooks: [
    {
      target: '[data-tour="webhook-create"]',
      title: "Create a subscription safely",
      description: "Create subscriptions with event filters and optional HMAC secret for verification.",
      side: "bottom",
    },
    {
      target: '[data-tour="webhook-list"]',
      title: "Manage active subscriptions",
      description: "Each subscription can be edited, paused, tested, and deleted in-place.",
      side: "top",
    },
    {
      target: '[data-tour="webhook-deliveries"]',
      title: "Inspect delivery history",
      description: "Delivery history includes status, latency, retries, and error messages.",
      side: "left",
    },
  ],
  event_templates: [
    {
      target: '[data-tour="template-library"]',
      title: "Start from proven templates",
      description: "Start from built-in templates for faster event setup.",
      side: "bottom",
    },
    {
      target: '[data-tour="event-clone"]',
      title: "Clone a previous event",
      description: "Clone an existing event to preserve structure while resetting participant state.",
      side: "bottom",
    },
    {
      target: '[data-tour="template-import-export"]',
      title: "Share blueprint JSON",
      description: "Import/export template JSON to share event blueprints across teams.",
      side: "top",
    },
  ],
  achievements: [
    {
      target: '[data-tour="achievement-create"]',
      title: "Define the achievement",
      description: "Define criteria and rewards to drive exploration and progression.",
      side: "bottom",
    },
    {
      target: '[data-tour="achievement-list"]',
      title: "Monitor unlock adoption",
      description: "Track unlock adoption and adjust criteria from one place.",
      side: "top",
    },
  ],
  workflows: [
    {
      target: '[data-tour="workflow-create"]',
      title: "Create from the workflow index",
      description: "Start with the builder card to create a new workflow or jump into the editor.",
      side: "left",
    },
    {
      target: '[data-tour="workflow-list"]',
      title: "Keep the workflow list clean",
      description: "This list is the operational source of truth for draft, published, and archived workflows.",
      side: "right",
    },
    {
      target: '[data-tour="workflow-runbooks"]',
      title: "Use runbooks for live briefs",
      description: "Runbooks turn graph logic into staff-ready instructions without leaving the workflow surface.",
      side: "left",
    },
  ],
};

let activeTourDriver: Driver | null = null;
let pendingAutoStartTimer: number | null = null;

export function tourStorageKey(tourName: string): string {
  return `${TOUR_STORAGE_PREFIX}${tourName}`;
}

export function getStoredTourDismissed(tourName: string): boolean {
  return localStore.getString(tourStorageKey(tourName), "") === "1";
}

export function setStoredTourDismissed(tourName: string): void {
  localStore.setString(tourStorageKey(tourName), "1");
}

function createHelpPanel(): HelpPanelElements {
  let panel = document.querySelector<HTMLElement>("[data-help-panel]");
  if (!(panel instanceof HTMLElement)) {
    panel = document.createElement("aside");
    panel.className = "help-panel";
    panel.setAttribute("data-help-panel", "1");
    panel.innerHTML = `
      <div class="help-panel-shell" role="dialog" aria-label="Page help">
        <div class="help-panel-toolbar">
          <h2>Page Help</h2>
          <button type="button" class="btn btn-secondary btn-sm" data-help-panel-close>Close</button>
        </div>
        <div class="help-panel-search-row">
          <input class="form-input" type="search" placeholder="Search help" data-help-panel-search>
          <button type="button" class="btn btn-secondary btn-sm" data-help-panel-search-btn>Search</button>
          <button type="button" class="btn btn-secondary btn-sm" data-help-panel-tour-btn>Start Tour</button>
        </div>
        <div class="help-panel-content" data-help-panel-content>
          <div class="subtitle">Loading help...</div>
        </div>
      </div>
      <button class="help-panel-backdrop" type="button" data-help-panel-close aria-label="Close"></button>
    `;
    document.body.appendChild(panel);
  }
  return {
    root: panel,
    content: panel.querySelector<HTMLElement>("[data-help-panel-content]"),
    search: panel.querySelector<HTMLInputElement>("[data-help-panel-search]"),
  };
}

async function fetchHelpHTML(query: string): Promise<string> {
  const url = new URL(HELP_ENDPOINT, window.location.origin);
  url.searchParams.set("page", window.location.pathname);
  if (query) {
    url.searchParams.set("q", query);
  }
  const response = await fetch(url.toString(), {
    credentials: "same-origin",
    headers: {
      Accept: "text/html",
    },
  });
  if (!response.ok) {
    throw new Error("help request failed");
  }
  return response.text();
}

function renderHelpPanelError(panel: HelpPanelElements, query: string): void {
  if (!(panel.content instanceof HTMLElement)) {
    return;
  }
  clearChildren(panel.content);
  panel.content.append(
    createElement("p", {
      className: "help-panel-error",
      text: "Could not load help content. Check your connection and try again.",
    }),
  );
  const retryBtn = createElement("button", {
    className: "btn btn-secondary btn-sm",
    text: "Retry",
    attrs: {
      type: "button",
      "data-help-panel-retry": "1",
    },
  });
  retryBtn.addEventListener("click", () => {
    void openHelpPanel(query);
  });
  panel.content.append(retryBtn);
}

export async function openHelpPanel(query = ""): Promise<void> {
  const panel = createHelpPanel();
  panel.root.classList.add("open");
  if (panel.search) {
    panel.search.value = query;
    panel.search.focus({ preventScroll: true });
  }
  if (panel.content instanceof HTMLElement) {
    panel.content.innerHTML = '<div class="subtitle">Loading help...</div>';
    try {
      panel.content.innerHTML = await fetchHelpHTML(query);
    } catch (err) {
      logger.warn("tours:help-panel-fetch", err, {
        page: window.location.pathname,
        query,
      });
      renderHelpPanelError(panel, query);
    }
  }
}

export function closeHelpPanel(): void {
  const panel = document.querySelector("[data-help-panel]");
  if (panel instanceof HTMLElement) {
    panel.classList.remove("open");
  }
}

export function currentTourName(pathname = window.location.pathname): string {
  const byData = String(document.body.dataset.tourPage || "").trim();
  if (byData) return byData;
  const path = pathname;
  if (path.startsWith("/gm/qrcodes")) return "qrcodes";
  if (path.startsWith("/gm/planner")) return "planner";
  if (path.startsWith("/gm/participants")) return "participants";
  if (path.startsWith("/gm/integrations/webhooks")) return "webhooks";
  if (path.startsWith("/gm/events/new")) return "event_templates";
  if (path.startsWith("/gm/achievements")) return "achievements";
  if (path.startsWith("/gm/workflows")) return "workflows";
  return "";
}

function resolveTourSteps(tourName: string): DriveStep[] {
  const steps: DriveStep[] = [];
  for (const step of TOUR_STEPS[tourName] || []) {
    const element = document.querySelector<HTMLElement>(step.target);
    if (!(element instanceof HTMLElement)) {
      continue;
    }
    steps.push({
      element,
      popover: {
        title: step.title,
        description: step.description,
        side: step.side || "bottom",
        align: step.align || "start",
        showButtons: ["previous", "next", "close"],
        progressText: "{{current}} / {{total}}",
        prevBtnText: "Back",
        nextBtnText: "Next",
        doneBtnText: "Done",
      },
    });
  }
  return steps;
}

async function persistTourCompletion(tourName: string): Promise<void> {
  setStoredTourDismissed(tourName);
  try {
    await fetch(`/api/me/tour/${encodeURIComponent(tourName)}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });
  } catch (_err) {
    // Ignore tracking failure.
  }
}

function cancelPendingAutoStart(): void {
  if (pendingAutoStartTimer !== null) {
    timers.clear(pendingAutoStartTimer);
    pendingAutoStartTimer = null;
  }
}

function addSkipButton(popover: PopoverDOM, onSkip: () => void): void {
  if (popover.footerButtons.querySelector(TOUR_SKIP_BUTTON_SELECTOR)) {
    return;
  }
  const skip = document.createElement("button");
  skip.type = "button";
  skip.className = "driver-popover-skip-btn";
  skip.setAttribute("data-tour-skip", "1");
  skip.textContent = "Skip tour";
  skip.addEventListener("click", onSkip);
  popover.footerButtons.insertBefore(skip, popover.footerButtons.firstChild);
}

export function startTour(tourName: string): void {
  const steps = resolveTourSteps(tourName);
  if (!steps.length) {
    return;
  }

  if (activeTourDriver && activeTourDriver.isActive()) {
    activeTourDriver.destroy();
    activeTourDriver = null;
  }

  let completionRequested = false;

  const markCompletedAndClose = (): void => {
    completionRequested = true;
    void persistTourCompletion(tourName);
    activeTourDriver?.destroy();
  };

  activeTourDriver = driver({
    animate: true,
    allowClose: true,
    smoothScroll: true,
    overlayOpacity: 0.72,
    stagePadding: 12,
    stageRadius: 14,
    showProgress: true,
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    showButtons: ["previous", "next", "close"],
    steps,
    onPopoverRender: (popover) => {
      addSkipButton(popover, () => {
        completionRequested = true;
        markCompletedAndClose();
      });
    },
    onNextClick: (_el, _step, opts) => {
      if (opts.driver.isLastStep()) {
        completionRequested = true;
        void persistTourCompletion(tourName);
        opts.driver.destroy();
        return;
      }
      opts.driver.moveNext();
    },
    onPrevClick: (_el, _step, opts) => {
      opts.driver.movePrevious();
    },
    onCloseClick: () => {
      markCompletedAndClose();
    },
    onDestroyed: () => {
      if (!completionRequested) {
        activeTourDriver = null;
        return;
      }
      activeTourDriver = null;
    },
  });

  activeTourDriver.drive();
}

export async function maybeAutoStartTour(): Promise<void> {
  const tourName = currentTourName();
  if (!tourName) return;
  if (!(TOUR_STEPS[tourName] || []).length) return;
  if (getStoredTourDismissed(tourName)) return;

  try {
    const response = await fetch(`/api/me/tour/${encodeURIComponent(tourName)}/status`, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });
    if (!response.ok) {
      logger.warn("tours:status-not-ok", new Error(`tour status returned ${response.status}`), {
        status: response.status,
        tour: tourName,
      });
      return;
    }
    const payload = await response.json();
    const completed = payload && payload.data ? payload.data.completed : true;
    if (completed) {
      setStoredTourDismissed(tourName);
      return;
    }
    cancelPendingAutoStart();
    pendingAutoStartTimer = timers.setTimeout(() => {
      pendingAutoStartTimer = null;
      if (activeTourDriver?.isActive()) {
        return;
      }
      startTour(tourName);
    }, 650);
  } catch (err) {
    logger.warn("tours:status-fetch", err, {
      page: window.location.pathname,
      tour: tourName,
    });
  }
}

function initSessionHandoffTimers(): void {
  document.querySelectorAll("[data-session-handoff]").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.dataset.handoffTimerInit === "1") return;
    node.dataset.handoffTimerInit = "1";
    const counter = node.querySelector("[data-handoff-countdown]");
    let left = Number.parseInt(String(node.dataset.expiresIn || "0"), 10) || 0;
    let tickHandle: number | null = null;
    const cleanup = () => {
      if (tickHandle !== null) {
        timers.clear(tickHandle);
        tickHandle = null;
      }
    };
    const observer = new MutationObserver(() => {
      if (!node.isConnected) {
        cleanup();
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const tick = () => {
      if (!(counter instanceof HTMLElement) || !node.isConnected) {
        cleanup();
        observer.disconnect();
        return;
      }
      counter.textContent = String(Math.max(0, left));
      left -= 1;
      if (left < 0) {
        node.classList.add("is-expired");
        cleanup();
        observer.disconnect();
        return;
      }
      tickHandle = timers.setTimeout(tick, 1000);
    };
    tick();
  });
}

export function bootTours(): void {
  const appWindow = window as WindowWithTourState;
  if (appWindow.__WMUToursBooted) {
    return;
  }
  appWindow.__WMUToursBooted = true;

  const suppressPendingAutoStart = () => {
    cancelPendingAutoStart();
  };

  document.addEventListener("pointerdown", suppressPendingAutoStart, true);
  document.addEventListener("keydown", suppressPendingAutoStart, true);

  document.addEventListener("click", (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    if (target.closest("[data-help-open]")) {
      event.preventDefault();
      void openHelpPanel("");
      return;
    }

    if (target.closest("[data-help-panel-close]")) {
      event.preventDefault();
      closeHelpPanel();
      return;
    }

    if (target.closest("[data-help-panel-search-btn]")) {
      event.preventDefault();
      const panel = createHelpPanel();
      const query = panel.search instanceof HTMLInputElement ? panel.search.value : "";
      void openHelpPanel(query);
      return;
    }

    if (target.closest("[data-help-panel-tour-btn]")) {
      event.preventDefault();
      startTour(currentTourName());
      return;
    }

  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeHelpPanel();
      return;
    }
    if (event.key !== "Enter") return;
    const panel = document.querySelector("[data-help-panel]");
    const active = document.activeElement;
    if (!(panel instanceof HTMLElement) || !panel.classList.contains("open")) return;
    if (!(active instanceof HTMLInputElement) || !active.matches("[data-help-panel-search]")) return;
    event.preventDefault();
    void openHelpPanel(active.value || "");
  });

  document.addEventListener("htmx:afterSwap", () => {
    initSessionHandoffTimers();
  });

  void maybeAutoStartTour();
  initSessionHandoffTimers();
}
